"use client";

import { File } from "lucide-react";
import {
  AdvancedList,
  type AdvancedListSortDef,
} from "@west-tennessee-consulting/ui";
import type { Attachment } from "./SaveAttachmentsToGoogleDriveModal.types";

interface AttachmentSelectionListProps {
  attachments: Attachment[];
  onDeselectAll: () => void;
  onSelectAll: () => void;
  onToggleAttachment: (attachmentId: string) => void;
  selectedAttachmentIds: Set<string>;
}

const SORTS: Array<AdvancedListSortDef<Attachment>> = [
  { id: "name", label: "Name", compare: (a, b) => a.name.localeCompare(b.name) },
  { id: "size", label: "Size", compare: (a, b) => (a.size ?? 0) - (b.size ?? 0) },
];

/**
 * Search + sort added via the shared AdvancedList primitive (packages/ui) —
 * this list has no upper bound on attachment count (a busy email thread can
 * carry dozens of inline images), and previously had no way to find one item
 * beyond scrolling a max-h-48 box. Selection semantics (checkbox toggle,
 * Select/Deselect All) are unchanged.
 */
export function AttachmentSelectionList({
  attachments,
  onDeselectAll,
  onSelectAll,
  onToggleAttachment,
  selectedAttachmentIds,
}: AttachmentSelectionListProps) {
  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <div className="p-4 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
          Attachments ({selectedAttachmentIds.size} of {attachments.length} selected):
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onSelectAll}
            className="text-xs px-2 py-1 rounded bg-blue-200 dark:bg-blue-900/50 hover:bg-blue-300 text-blue-900 dark:text-blue-200 font-medium"
          >
            Select All
          </button>
          <button
            type="button"
            onClick={onDeselectAll}
            className="text-xs px-2 py-1 rounded bg-blue-200 dark:bg-blue-900/50 hover:bg-blue-300 text-blue-900 dark:text-blue-200 font-medium"
          >
            Deselect All
          </button>
        </div>
      </div>
      <AdvancedList<Attachment>
        items={attachments}
        getId={(att) => att.id}
        searchFields={(att) => [att.name, att.mimeType]}
        searchPlaceholder="Search attachments…"
        sorts={SORTS}
        maxHeight="12rem"
        renderItem={(att) => (
          <label className="flex items-center gap-3 p-2 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40 cursor-pointer transition-colors">
            <input
              type="checkbox"
              checked={selectedAttachmentIds.has(att.id)}
              onChange={() => onToggleAttachment(att.id)}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-700 text-blue-600 focus:ring-blue-500 flex-shrink-0"
            />
            <File className="h-4 w-4 text-blue-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-blue-900 dark:text-blue-200 truncate font-medium">
                {att.name}
              </div>
              {att.size ? (
                <div className="text-xs text-blue-700 dark:text-blue-300">
                  {(att.size / 1024).toFixed(1)} KB
                </div>
              ) : null}
            </div>
          </label>
        )}
      />
    </div>
  );
}
